import { IComparisonConfig, TimeUnit } from './apollo/graphqlSchema';
import moment from 'moment/moment';
import { getCapitalizedTimeUnitText } from './timeUnits';
import { globalFormat } from './formatting';
import { IDateInput } from '../redux/slices/oldBuilder/date/types';
import { isNil } from 'lodash';
import { UnreachableCaseError } from 'ts-essentials';

export const comparisonConfigDisplay = (
  comparisonConfig: IComparisonConfig,
): string => {
  const { timeUnit, offset } = comparisonConfig;
  const absOffset = Math.abs(offset);
  const prevNext = offset < 0 ? 'Next' : 'Previous';
  const timeUnitHuman = getCapitalizedTimeUnitText(timeUnit);
  if (absOffset <= 1) {
    switch (timeUnit) {
      case TimeUnit.Day:
      case TimeUnit.Week:
      case TimeUnit.Month:
      case TimeUnit.Quarter:
      case TimeUnit.Year:
        return `${prevNext} ${timeUnitHuman.singular}`;
      case TimeUnit.FourWeek:
      case TimeUnit.AllTime:
      case TimeUnit.Hour:
      case TimeUnit.Minute:
      case TimeUnit.Na:
      case TimeUnit.Second:
        console.error('Unsupported comparison time unit:', timeUnit);
    }
  } else {
    const agoAfter = offset < 0 ? 'after' : 'ago';
    switch (timeUnit) {
      case TimeUnit.Day:
      case TimeUnit.Week:
      case TimeUnit.Month:
      case TimeUnit.Quarter:
      case TimeUnit.Year:
        return `${absOffset} ${timeUnitHuman.plural} ${agoAfter}`;
      case TimeUnit.FourWeek:
      case TimeUnit.AllTime:
      case TimeUnit.Hour:
      case TimeUnit.Minute:
      case TimeUnit.Na:
      case TimeUnit.Second:
        throw new Error('Unsupported comparison time unit: ' + timeUnit);
      default:
        throw new UnreachableCaseError(timeUnit);
    }
  }
  return prevNext; // fallback for unsupported time units
};

// calculate the comparison range end date based on the comparison config and selected end date.
export const getComparisonEndDate = (
  comparisonConfig: IComparisonConfig | null,
  queryEnd: string,
): Date | null => {
  if (!comparisonConfig) {
    return null;
  }
  let subtractUnit: 'days' | 'weeks' | 'months' | 'years' = 'days';
  let offset = comparisonConfig.offset;
  switch (comparisonConfig.timeUnit) {
    case TimeUnit.Year:
      subtractUnit = 'years';
      break;
    case TimeUnit.Quarter:
      subtractUnit = 'months';
      offset = offset * 3;
      break;
    case TimeUnit.Month:
      subtractUnit = 'months';
      break;
    case TimeUnit.FourWeek:
      subtractUnit = 'weeks';
      offset = offset * 4;
      break;
    case TimeUnit.Week:
      subtractUnit = 'weeks';
      break;
    case TimeUnit.Day:
      // offset and subtractUnit already set
      break;
    // these below are not supported - we will fall back to the day subtractUnit after logging the error
    case TimeUnit.AllTime:
    case TimeUnit.Na:
    case TimeUnit.Hour:
    case TimeUnit.Minute:
    case TimeUnit.Second:
      throw new Error(
        'Unsupported comparison time unit: ' + comparisonConfig.timeUnit,
      );
    default:
      throw new UnreachableCaseError(comparisonConfig.timeUnit);
  }
  return moment(queryEnd).subtract(offset, subtractUnit).toDate();
};

// calculate the comparison range start date based on the comparison range end date
// and the primary date range
const getComparisonStartDate = (
  comparsionEnd: Date,
  queryStart: string,
  queryEnd: string,
): Date => {
  const originalRangeStart = moment(queryStart);
  const originalRangeEnd = moment(queryEnd);
  const diff = originalRangeEnd.diff(originalRangeStart, 'days');
  return moment(comparsionEnd).subtract(diff, 'days').toDate();
};

export const getComparisonRangeDisplay = (
  cc: IComparisonConfig,
  dateInput: IDateInput,
) => {
  const { dateFormat } = globalFormat.getGlobalFormat();
  const message = [comparisonConfigDisplay(cc)];
  const { startDate, endDate, datePicker } = dateInput;

  if (isNil(datePicker) || datePicker.__typename === 'DatePickerFixed') {
    const queryStart = datePicker?.start ?? startDate;
    const queryEnd = datePicker?.end ?? endDate;
    const selectedCalendarDate =
      getComparisonEndDate(cc, queryEnd) ?? moment(queryEnd).toDate();
    const comparisonStartDate = getComparisonStartDate(
      selectedCalendarDate,
      queryStart,
      queryEnd,
    );
    message.push(globalFormat.formatDate(comparisonStartDate, { dateFormat }));
    message.push(globalFormat.formatDate(selectedCalendarDate, { dateFormat }));
  }

  return message.join(' - ');
};
